import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Refine } from '@refinedev/core';
import routerBindings from '@refinedev/react-router';
import { SessionProvider } from './lib/session.js';
import { ThemeProvider } from './lib/theme.js';
import { BrandProvider } from './lib/brand.js';
import { dataProvider } from './lib/dataProvider.js';
import { RequireAuth } from './components/RequireAuth.js';
import { LoginPage } from './routes/auth/LoginPage.js';
import { ResetPassword } from './routes/auth/ResetPassword.js';
import { RedeemInvite } from './routes/invite/RedeemInvite.js';
import { StaffLayout } from './routes/staff/StaffLayout.js';
import { StaffDashboard } from './routes/staff/StaffDashboard.js';
import { PropertyList } from './routes/staff/properties/PropertyList.js';
import { PropertyForm } from './routes/staff/properties/PropertyForm.js';
import { PropertyShow } from './routes/staff/properties/PropertyShow.js';
import { UnitForm } from './routes/staff/properties/UnitForm.js';
import { UnitShow } from './routes/staff/properties/UnitShow.js';
import { OwnershipForm } from './routes/staff/properties/OwnershipForm.js';
import { OwnerList } from './routes/staff/owners/OwnerList.js';
import { OwnerForm } from './routes/staff/owners/OwnerForm.js';
import { TenantList } from './routes/staff/tenants/TenantList.js';
import { TenantForm } from './routes/staff/tenants/TenantForm.js';
import { LeaseList } from './routes/staff/leases/LeaseList.js';
import { LeaseWizard } from './routes/staff/leases/LeaseWizard.js';
import { LeaseShow } from './routes/staff/leases/LeaseShow.js';
import { ChecklistForm } from './routes/staff/leases/ChecklistForm.js';
import { RentRoll } from './routes/staff/ledger/RentRoll.js';
import { Delinquency } from './routes/staff/ledger/Delinquency.js';
import { OpenChargesByLease } from './routes/staff/ledger/OpenChargesByLease.js';
import { ReportsHome } from './routes/staff/reports/ReportsHome.js';
import { VendorList } from './routes/staff/vendors/VendorList.js';
import { VendorForm } from './routes/staff/vendors/VendorForm.js';
import { WorkOrderList } from './routes/staff/work-orders/WorkOrderList.js';
import { WorkOrderForm } from './routes/staff/work-orders/WorkOrderForm.js';
import { WorkOrderShow } from './routes/staff/work-orders/WorkOrderShow.js';
import { AuditLog } from './routes/staff/AuditLog.js';
import { ImportHome } from './routes/staff/import/ImportHome.js';
import { ImportBuildium } from './routes/staff/import/ImportBuildium.js';
import { ImportCsv } from './routes/staff/import/ImportCsv.js';
import { ImportManual } from './routes/staff/import/ImportManual.js';
import { PortalLayout } from './routes/portal/PortalLayout.js';
import { PortalHome } from './routes/portal/PortalHome.js';
import { PortalDocuments } from './routes/portal/PortalDocuments.js';
import { RequirePortalActor } from './routes/portal/RequirePortalActor.js';
import { OwnerHome } from './routes/portal/owner/OwnerHome.js';
import { OwnerPropertyList } from './routes/portal/owner/OwnerPropertyList.js';
import { OwnerPropertyShow } from './routes/portal/owner/OwnerPropertyShow.js';
import { OwnerResidents } from './routes/portal/owner/OwnerResidents.js';
import { TenantHome } from './routes/portal/tenant/TenantHome.js';
import { PayRent } from './routes/portal/tenant/PayRent.js';
import { MaintenanceList } from './routes/portal/tenant/MaintenanceList.js';
import { MaintenanceRequestForm } from './routes/portal/tenant/MaintenanceRequestForm.js';
import { MaintenanceRequestDetail } from './routes/portal/tenant/MaintenanceRequestDetail.js';
import { ApplyLayout } from './routes/apply/ApplyLayout.js';
import { RequireApplicant } from './routes/apply/RequireApplicant.js';
import { PreQualification } from './routes/apply/PreQualification.js';
import { ApplicantSignUp } from './routes/apply/ApplicantSignUp.js';
import { ApplicationDraft } from './routes/apply/ApplicationDraft.js';
import { ApplicationFee } from './routes/apply/ApplicationFee.js';
import { ApplicationSubmit } from './routes/apply/ApplicationSubmit.js';
import { ApplicationQueue } from './routes/staff/applications/ApplicationQueue.js';
import { ApplicationShow } from './routes/staff/applications/ApplicationShow.js';
import { ScreeningCriterionList } from './routes/staff/screening-criteria/ScreeningCriterionList.js';
import { CompanyInformationPage } from './routes/staff/CompanyInformationPage.js';
import { CompliancePagesPage } from './routes/staff/CompliancePagesPage.js';
import { FeeSchedulePage } from './routes/staff/FeeSchedulePage.js';
import { DocumentTemplateList } from './routes/staff/document-templates/DocumentTemplateList.js';
import { TestEmails } from './routes/staff/developer/TestEmails.js';
import { OwnerDisbursementRunPage } from './routes/staff/disbursements/OwnerDisbursementRunPage.js';
import { OwnerStatementListPage } from './routes/staff/disbursements/OwnerStatementListPage.js';
import { VendorDisbursementRunPage } from './routes/staff/vendor-disbursements/VendorDisbursementRunPage.js';
import { PaymentsHistory } from './routes/staff/PaymentsHistory.js';
import { MaintenanceReports } from './routes/staff/MaintenanceReports.js';
import { OwnerStatementsPage } from './routes/portal/owner/OwnerStatementsPage.js';
import { OwnerPayoutsPage } from './routes/portal/owner/OwnerPayoutsPage.js';
import { VendorHome } from './routes/portal/vendor/VendorHome.js';
import { VendorWorkOrderList } from './routes/portal/vendor/VendorWorkOrderList.js';
import { VendorPayoutsPage } from './routes/portal/vendor/VendorPayoutsPage.js';
import { VendorDisbursementsPage } from './routes/portal/vendor/VendorDisbursementsPage.js';
import { PublicLayout } from './routes/public/PublicLayout.js';
import { PublicHomepage } from './routes/public/PublicHomepage.js';
import { RentalFinder } from './routes/public/RentalFinder.js';
import { NewRentals } from './routes/public/NewRentals.js';
import { PublicListingDetail } from './routes/public/PublicListingDetail.js';
import { HirePropertyManager } from './routes/public/HirePropertyManager.js';
import { CompliancePage } from './routes/public/CompliancePage.js';

/**
 * Four route trees over one database:
 *
 *   /staff    full portfolio, authenticated staff only
 *   /portal   tenants and owners, scoped by RLS
 *   /apply    applicants — unauthenticated by default (step 10)
 *   /         the public listings site — always unauthenticated (step 11)
 *
 * Separate trees rather than one with conditional rendering, because a conditional
 * is something you can get wrong. The real boundary is in Postgres — a tenant
 * reaching /staff sees an empty console, not someone else's data — but there is no
 * reason to make that the first line of defence.
 *
 * ---------------------------------------------------------------------------
 * Refine wraps every tree, not just the staff one
 * ---------------------------------------------------------------------------
 *
 * `<Refine>` has to be an ancestor of every component calling a Refine hook, and the
 * portal calls the same `useList` against the same endpoints staff does. There is
 * no per-audience data provider, because the endpoints already return each actor's
 * own scope — so the provider sits above every tree. `/apply` and `/` are actor-less
 * and simply never call a Refine or session hook, rather than being structured
 * outside the provider nesting — see PublicLayout.tsx's own comment.
 *
 * `resources` is deliberately NOT configured. Its job is to power Refine's own menu
 * components and automatic route inference, and with no UI kit installed nothing
 * consumes it — it would be a second place to edit whenever a route changed, with
 * nothing reading it. Routes are declared here; hooks name their resource.
 */
export function App() {
  return (
    <BrandProvider>
      <BrowserRouter>
        <Refine
          dataProvider={dataProvider}
          routerProvider={routerBindings}
          options={{
            disableTelemetry: true,
            reactQuery: {
              clientConfig: {
                defaultOptions: {
                  queries: {
                    // A staff member editing inventory has to see their own write
                    // reflected, not a list cached thirty seconds ago.
                    staleTime: 0,
                    // No retries. A 403 from requireStaff or a 404 from an
                    // out-of-scope row is a final answer, and retrying it three times
                    // turns one honest refusal into three audit entries.
                    retry: false,
                  },
                },
              },
            },
          }}
        >
          <SessionProvider>
            <ThemeProvider>
              <Routes>
                {/* Four doors, one credential check. See LoginPage. */}
                <Route path="/login" element={<LoginPage door="resident" />} />
                <Route path="/login/owner" element={<LoginPage door="owner" />} />
                <Route path="/staff/login" element={<LoginPage door="staff" />} />
                <Route path="/staff/login/admin" element={<LoginPage door="admin" />} />
                <Route path="/invite/:token" element={<RedeemInvite />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route
                  path="/staff"
                  element={
                    <RequireAuth require="staff">
                      <StaffLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<StaffDashboard />} />

                  {/* Static segments before :id, so "new" is never read as an id. */}
                  <Route path="properties" element={<PropertyList />} />
                  <Route path="properties/new" element={<PropertyForm />} />
                  <Route path="properties/:id" element={<PropertyShow />} />
                  <Route path="properties/:id/edit" element={<PropertyForm />} />
                  <Route path="properties/:propertyId/units/new" element={<UnitForm />} />
                  <Route path="properties/:id/ownerships/new" element={<OwnershipForm />} />
                  <Route path="units/:id" element={<UnitShow />} />
                  <Route path="units/:id/edit" element={<UnitForm />} />

                  <Route path="owners" element={<OwnerList />} />
                  <Route path="owners/new" element={<OwnerForm />} />
                  <Route path="owners/:id/edit" element={<OwnerForm />} />

                  <Route path="tenants" element={<TenantList />} />
                  <Route path="tenants/new" element={<TenantForm />} />
                  <Route path="tenants/:id/edit" element={<TenantForm />} />

                  <Route path="leases" element={<LeaseList />} />
                  <Route path="leases/new" element={<LeaseWizard />} />
                  <Route path="leases/:id" element={<LeaseShow />} />
                  <Route path="leases/:leaseId/checklists/new" element={<ChecklistForm />} />

                  <Route path="rent-roll" element={<RentRoll />} />
                  <Route path="delinquency" element={<Delinquency />} />
                  {/* Reached from the dashboard's "Open charges" stat, not the nav. */}
                  <Route path="open-charges" element={<OpenChargesByLease />} />
                  <Route path="owner-disbursements" element={<OwnerDisbursementRunPage />} />
                  <Route path="owner-statements" element={<OwnerStatementListPage />} />
                  <Route path="vendor-disbursements" element={<VendorDisbursementRunPage />} />
                  <Route path="payments-history" element={<PaymentsHistory />} />

                  {/* One dynamic interface, not six routed pages — ReportsHome
                      switches its body on `?tab=`, it never navigates. */}
                  <Route path="reports" element={<ReportsHome />} />

                  <Route path="vendors" element={<VendorList />} />
                  <Route path="vendors/new" element={<VendorForm />} />
                  <Route path="vendors/:id/edit" element={<VendorForm />} />

                  <Route path="work-orders" element={<WorkOrderList />} />
                  <Route path="work-orders/new" element={<WorkOrderForm />} />
                  <Route path="work-orders/:id" element={<WorkOrderShow />} />

                  {/* reports:read, not maintenance:read — see its own nav
                      comment in StaffLayout.tsx. */}
                  <Route path="maintenance-reports" element={<MaintenanceReports />} />

                  {/* ADMIN in practice, but registered for every staff actor. The
                    refusal comes from `audit:read` at the API, and the page renders
                    it — a route that exists and is refused is more honest than a
                    route that 404s for reasons the client decided. */}
                  <Route path="audit-log" element={<AuditLog />} />

                  <Route path="import" element={<ImportHome />} />
                  <Route path="import/buildium" element={<ImportBuildium />} />
                  <Route path="import/csv" element={<ImportCsv />} />
                  <Route path="import/manual" element={<ImportManual />} />

                  <Route path="applications" element={<ApplicationQueue />} />
                  <Route path="applications/:id" element={<ApplicationShow />} />
                  <Route path="screening-criteria" element={<ScreeningCriterionList />} />

                  <Route path="company-information" element={<CompanyInformationPage />} />
                  <Route path="fee-schedule" element={<FeeSchedulePage />} />
                  <Route path="compliance-pages" element={<CompliancePagesPage />} />
                  <Route path="document-templates" element={<DocumentTemplateList />} />
                  <Route path="developer/test-emails" element={<TestEmails />} />
                </Route>

                <Route
                  path="/portal"
                  element={
                    <RequireAuth require="portal">
                      <PortalLayout />
                    </RequireAuth>
                  }
                >
                  {/* Three portals under one guard. `RequireAuth require="portal"` keeps
                    staff out; which of the three branches you land on is decided by
                    actor type in PortalHome, and getting it wrong lands you on a page
                    whose queries return nothing rather than on someone else's data. */}
                  <Route index element={<PortalHome />} />

                  <Route path="owner" element={<RequirePortalActor actorType="OWNER" />}>
                    <Route index element={<OwnerHome />} />
                    {/* Static segment before :id, same reason as the staff tree above. */}
                    <Route path="properties" element={<OwnerPropertyList />} />
                    <Route path="properties/:id" element={<OwnerPropertyShow />} />
                    <Route path="residents" element={<OwnerResidents />} />
                    <Route path="statements" element={<OwnerStatementsPage />} />
                    <Route path="payouts" element={<OwnerPayoutsPage />} />
                    <Route path="documents" element={<PortalDocuments />} />
                  </Route>

                  <Route path="tenant" element={<RequirePortalActor actorType="TENANT" />}>
                    <Route index element={<TenantHome />} />
                    <Route path="pay" element={<PayRent />} />
                    {/* Static segment before :id, same reason as the owner tree above. */}
                    <Route path="maintenance" element={<MaintenanceList />} />
                    <Route path="maintenance/new" element={<MaintenanceRequestForm />} />
                    <Route path="maintenance/:id" element={<MaintenanceRequestDetail />} />
                    <Route path="documents" element={<PortalDocuments />} />
                  </Route>

                  {/* Step 14 — a vendor's own portal. Read-only plus one WRITE
                    (Connect enrollment), the same shape OWNER/TENANT already
                    have, so this fits RequirePortalActor directly rather than
                    needing its own top-level tree the way /apply did. */}
                  <Route path="vendor" element={<RequirePortalActor actorType="VENDOR" />}>
                    <Route index element={<VendorHome />} />
                    <Route path="work-orders" element={<VendorWorkOrderList />} />
                    <Route path="payouts" element={<VendorPayoutsPage />} />
                    <Route path="disbursements" element={<VendorDisbursementsPage />} />
                  </Route>
                </Route>

                {/* Step 10 — a separate top-level tree, not a branch of /portal.
                    RequireAuth hard-requires signed-in with no path for an
                    anonymous visitor (needed for pre-qualification and
                    sign-up, both unauthenticated) — APPLICANT breaks the
                    read-only rule every other portal actor follows, which is
                    what actually rules out RequirePortalActor here, not its
                    type union (VENDOR fit that union fine in step 14; an
                    unauthenticated pre-qualification step never will). See
                    RequireApplicant. */}
                <Route path="/apply" element={<ApplyLayout />}>
                  <Route index element={<PreQualification />} />
                  <Route path="sign-up" element={<ApplicantSignUp />} />
                  <Route element={<RequireApplicant />}>
                    <Route path=":id" element={<ApplicationDraft />} />
                    <Route path=":id/fee" element={<ApplicationFee />} />
                    <Route path=":id/submit" element={<ApplicationSubmit />} />
                  </Route>
                </Route>

                {/* Step 11 — the public listings site, the first
                    unauthenticated front door in the app. `/` used to
                    redirect straight into /portal; now it's real, and the
                    catch-all follows it rather than the auth-gated area,
                    since an unmatched path is more likely a mistyped public
                    URL than a staff/portal one. Same posture as /apply:
                    nested inside Refine/SessionProvider mechanically (one
                    <Routes> tree), never calling either's hooks. */}
                <Route path="/" element={<PublicLayout />}>
                  <Route index element={<PublicHomepage />} />
                  <Route path="new-rentals" element={<NewRentals />} />
                  <Route path="listings" element={<RentalFinder />} />
                  <Route path="listings/:id" element={<PublicListingDetail />} />
                  <Route path="hire-a-property-manager" element={<HirePropertyManager />} />
                  <Route path="compliance" element={<CompliancePage page="compliance" />} />
                  <Route path="privacy-policy" element={<CompliancePage page="privacy-policy" />} />
                  <Route path="terms-of-use" element={<CompliancePage page="terms-of-use" />} />
                  <Route path="equal-housing" element={<CompliancePage page="equal-housing" />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ThemeProvider>
          </SessionProvider>
        </Refine>
      </BrowserRouter>
    </BrandProvider>
  );
}
